import type {
  AttributeDefinition,
  AudienceDefinition,
  ContentTypeDefinition,
  Floor,
  Genre,
} from "@/types/taxonomy";

/**
 * 分類マスタ（デモデータ）。
 * 実データ連携後は、このファイルの内容を外部APIのレスポンスに置き換える。
 * ジャンルはソースコード上の一覧に依存せず、ここから動的に生成される。
 */

export const floors: Floor[] = [
  {
    id: "fl-story",
    name: "ストーリー・題材",
    providerFloorId: "FL-01",
    description: "物語の題材や展開の傾向から探すフロアです。",
    sortOrder: 1,
  },
  {
    id: "fl-char",
    name: "キャラクター・関係性",
    providerFloorId: "FL-02",
    description: "登場人物の役割や人間関係から探すフロアです。",
    sortOrder: 2,
  },
  {
    id: "fl-mood",
    name: "雰囲気・読み味",
    providerFloorId: "FL-03",
    description: "作品の空気感やボリューム、季節感から探すフロアです。",
    sortOrder: 3,
  },
  {
    id: "fl-media",
    name: "メディア・収録形式",
    providerFloorId: "FL-04",
    description: "ドラマCDや写真集など、収録形式から探すフロアです。",
    sortOrder: 4,
  },
  {
    id: "fl-collect",
    name: "グッズ・コレクション",
    providerFloorId: "FL-05",
    description: "商品タイプがグッズの作品をまとめたフロアです。",
    sortOrder: 5,
  },
];

export const genres: Genre[] = [
  {
    id: "g-fantasy",
    name: "ファンタジー",
    kana: "ふぁんたじー",
    aliases: ["空想", "異世界", "幻想"],
    providerGenreId: "GN-1001",
    providerFloorId: "FL-01",
    floorId: "fl-story",
    audienceIds: ["male", "female", "common"],
    contentTypeIds: [],
    description: "架空の世界観や不思議な設定を軸にした作品。",
    sortOrder: 1,
  },
  {
    id: "g-adventure",
    name: "冒険",
    kana: "ぼうけん",
    aliases: ["旅", "探索"],
    providerGenreId: "GN-1002",
    providerFloorId: "FL-01",
    floorId: "fl-story",
    audienceIds: ["male", "female", "common"],
    contentTypeIds: [],
    description: "未知の場所や目的へ向かう旅を描いた作品。",
    sortOrder: 2,
  },
  {
    id: "g-action",
    name: "アクション",
    kana: "あくしょん",
    aliases: ["バトルアクション", "活劇"],
    providerGenreId: "GN-1003",
    providerFloorId: "FL-01",
    floorId: "fl-story",
    audienceIds: ["male", "common"],
    contentTypeIds: [],
    description: "戦いや追跡など、動きのある場面が中心の作品。",
    sortOrder: 3,
  },
  {
    id: "g-sf",
    name: "SF",
    kana: "えすえふ",
    aliases: ["サイエンスフィクション", "SF作品"],
    providerGenreId: "GN-1004",
    providerFloorId: "FL-01",
    floorId: "fl-story",
    audienceIds: ["male", "common"],
    contentTypeIds: [],
    description: "科学技術や未来社会を題材にした作品。",
    sortOrder: 4,
  },
  {
    id: "g-mystery",
    name: "ミステリー",
    kana: "みすてりー",
    aliases: ["推理", "謎解き"],
    providerGenreId: "GN-1005",
    providerFloorId: "FL-01",
    floorId: "fl-story",
    audienceIds: ["male", "female", "common"],
    contentTypeIds: [],
    description: "謎の提示と解決を軸にした作品。",
    sortOrder: 5,
  },
  {
    id: "g-suspense",
    name: "サスペンス",
    kana: "さすぺんす",
    aliases: ["緊張感", "スリラー"],
    providerGenreId: "GN-1006",
    providerFloorId: "FL-01",
    floorId: "fl-story",
    audienceIds: ["male", "common"],
    contentTypeIds: [],
    description: "先の読めない緊張感を楽しむ作品。",
    sortOrder: 6,
  },
  {
    id: "g-horror",
    name: "ホラー",
    kana: "ほらー",
    aliases: ["怪奇", "心霊"],
    providerGenreId: "GN-1007",
    providerFloorId: "FL-01",
    floorId: "fl-story",
    audienceIds: ["male", "common"],
    contentTypeIds: [],
    description: "恐怖や不安を主題にした作品。",
    sortOrder: 7,
  },
  {
    id: "g-occult",
    name: "オカルト",
    kana: "おかると",
    aliases: ["都市伝説", "超常現象"],
    providerGenreId: "GN-1008",
    providerFloorId: "FL-01",
    floorId: "fl-story",
    audienceIds: ["male", "common"],
    contentTypeIds: [],
    description: "超常現象や言い伝えを扱う作品。",
    sortOrder: 8,
  },
  {
    id: "g-history",
    name: "歴史",
    kana: "れきし",
    aliases: ["時代劇", "戦国", "時代物"],
    providerGenreId: "GN-1009",
    providerFloorId: "FL-01",
    floorId: "fl-story",
    audienceIds: ["male", "common"],
    contentTypeIds: [],
    description: "過去の時代を舞台にした作品。",
    sortOrder: 9,
  },
  {
    id: "g-romance",
    name: "恋愛",
    kana: "れんあい",
    aliases: ["ラブ", "恋", "ラブストーリー"],
    providerGenreId: "GN-1010",
    providerFloorId: "FL-01",
    floorId: "fl-story",
    audienceIds: ["male", "female", "common"],
    contentTypeIds: [],
    description: "恋愛感情や関係の変化を描いた作品。",
    sortOrder: 10,
  },
  {
    id: "g-comedy",
    name: "コメディ",
    kana: "こめでぃ",
    aliases: ["お笑い", "ラブコメ", "喜劇"],
    providerGenreId: "GN-1011",
    providerFloorId: "FL-01",
    floorId: "fl-story",
    audienceIds: ["male", "female", "common"],
    contentTypeIds: [],
    description: "会話や展開の軽妙さを楽しむ作品。",
    sortOrder: 11,
  },
  {
    id: "g-drama",
    name: "ドラマ",
    kana: "どらま",
    aliases: ["ヒューマンドラマ", "人間ドラマ"],
    providerGenreId: "GN-1012",
    providerFloorId: "FL-01",
    floorId: "fl-story",
    audienceIds: ["male", "female", "common"],
    contentTypeIds: [],
    description: "人物の感情や関係を丁寧に描いた作品。",
    sortOrder: 12,
  },
  {
    id: "g-youth",
    name: "青春",
    kana: "せいしゅん",
    aliases: ["青春もの", "学園青春"],
    providerGenreId: "GN-1013",
    providerFloorId: "FL-01",
    floorId: "fl-story",
    audienceIds: ["male", "female", "common"],
    contentTypeIds: [],
    description: "成長期の時間や感情を描いた作品。",
    sortOrder: 13,
  },
  {
    id: "g-daily",
    name: "日常",
    kana: "にちじょう",
    aliases: ["日常系", "暮らし"],
    providerGenreId: "GN-1014",
    providerFloorId: "FL-01",
    floorId: "fl-story",
    audienceIds: ["female", "common"],
    contentTypeIds: [],
    description: "大きな事件よりも日々の暮らしを描いた作品。",
    sortOrder: 14,
  },
  {
    id: "g-healing",
    name: "ほのぼの",
    kana: "ほのぼの",
    aliases: ["癒やし", "いやし"],
    providerGenreId: "GN-1015",
    providerFloorId: "FL-01",
    floorId: "fl-story",
    audienceIds: ["female", "common"],
    contentTypeIds: [],
    description: "穏やかな空気感で読める作品。",
    sortOrder: 15,
  },
  {
    id: "g-dark",
    name: "ダーク",
    kana: "だーく",
    aliases: ["暗い", "ヘビー"],
    providerGenreId: "GN-1016",
    providerFloorId: "FL-01",
    floorId: "fl-story",
    audienceIds: ["male", "common"],
    contentTypeIds: [],
    description: "重い主題や陰影のある展開を含む作品。",
    sortOrder: 16,
  },
  {
    id: "g-emotion",
    name: "感動",
    kana: "かんどう",
    aliases: ["泣ける", "余韻"],
    providerGenreId: "GN-1017",
    providerFloorId: "FL-01",
    floorId: "fl-story",
    audienceIds: ["male", "female", "common"],
    contentTypeIds: [],
    description: "感情を動かす場面が印象に残る作品。",
    sortOrder: 17,
  },
  {
    id: "g-school",
    name: "学園",
    kana: "がくえん",
    aliases: ["学校", "スクール"],
    providerGenreId: "GN-1018",
    providerFloorId: "FL-01",
    floorId: "fl-story",
    audienceIds: ["male", "female", "common"],
    contentTypeIds: [],
    description: "学校を舞台にした作品。",
    sortOrder: 18,
  },
  {
    id: "g-office",
    name: "オフィス",
    kana: "おふぃす",
    aliases: ["職場", "会社", "お仕事"],
    providerGenreId: "GN-1019",
    providerFloorId: "FL-01",
    floorId: "fl-story",
    audienceIds: ["female", "common"],
    contentTypeIds: [],
    description: "職場の人間関係や仕事を描いた作品。",
    sortOrder: 19,
  },
  {
    id: "g-battle",
    name: "バトル",
    kana: "ばとる",
    aliases: ["対決", "勝負"],
    providerGenreId: "GN-1020",
    providerFloorId: "FL-01",
    floorId: "fl-story",
    audienceIds: ["male", "common"],
    contentTypeIds: [],
    description: "実力や駆け引きによる勝負を描いた作品。",
    sortOrder: 20,
  },
  {
    id: "g-mindgame",
    name: "頭脳戦",
    kana: "ずのうせん",
    aliases: ["心理戦", "駆け引き"],
    providerGenreId: "GN-1021",
    providerFloorId: "FL-01",
    floorId: "fl-story",
    audienceIds: ["male", "common"],
    contentTypeIds: [],
    description: "知略や読み合いを中心にした作品。",
    sortOrder: 21,
  },
  {
    id: "g-robot",
    name: "ロボット",
    kana: "ろぼっと",
    aliases: ["メカ", "機械"],
    providerGenreId: "GN-1022",
    providerFloorId: "FL-01",
    floorId: "fl-story",
    audienceIds: ["male", "common"],
    contentTypeIds: ["video", "manga", "game"],
    description: "機械やロボットが重要な要素の作品。",
    sortOrder: 22,
  },
  {
    id: "g-cooking",
    name: "料理",
    kana: "りょうり",
    aliases: ["グルメ", "food"],
    providerGenreId: "GN-1023",
    providerFloorId: "FL-01",
    floorId: "fl-story",
    audienceIds: ["female", "common"],
    contentTypeIds: [],
    description: "料理や食卓を物語の中心に置いた作品。",
    sortOrder: 23,
  },
  {
    id: "g-music",
    name: "音楽",
    kana: "おんがく",
    aliases: ["バンド", "ライブ"],
    providerGenreId: "GN-1024",
    providerFloorId: "FL-01",
    floorId: "fl-story",
    audienceIds: ["male", "female", "common"],
    contentTypeIds: [],
    description: "音楽や演奏を主題にした作品。",
    sortOrder: 24,
  },
  {
    id: "g-ensemble",
    name: "群像劇",
    kana: "ぐんぞうげき",
    aliases: ["アンサンブル", "多視点"],
    providerGenreId: "GN-2001",
    providerFloorId: "FL-02",
    floorId: "fl-char",
    audienceIds: ["male", "female", "common"],
    contentTypeIds: [],
    description: "複数の人物の視点や物語が交差する作品。",
    sortOrder: 1,
  },
  {
    id: "g-hero",
    name: "ヒーロー",
    kana: "ひーろー",
    aliases: ["主人公", "英雄"],
    providerGenreId: "GN-2002",
    providerFloorId: "FL-02",
    floorId: "fl-char",
    audienceIds: ["male", "common"],
    contentTypeIds: [],
    description: "中心となる主人公の活躍を描いた作品。",
    sortOrder: 2,
  },
  {
    id: "g-heroine",
    name: "ヒロイン",
    kana: "ひろいん",
    aliases: ["女主角", "主人公女子"],
    providerGenreId: "GN-2003",
    providerFloorId: "FL-02",
    floorId: "fl-char",
    audienceIds: ["female", "common"],
    contentTypeIds: [],
    description: "女性主人公の視点で進む作品。",
    sortOrder: 3,
  },
  {
    id: "g-partner",
    name: "相棒",
    kana: "あいぼう",
    aliases: ["バディ", "二人組"],
    providerGenreId: "GN-2004",
    providerFloorId: "FL-02",
    floorId: "fl-char",
    audienceIds: ["male", "female", "common"],
    contentTypeIds: [],
    description: "二人の関係性を軸にした作品。",
    sortOrder: 4,
  },
  {
    id: "g-family",
    name: "家族",
    kana: "かぞく",
    aliases: ["家庭", "親子"],
    providerGenreId: "GN-2005",
    providerFloorId: "FL-02",
    floorId: "fl-char",
    audienceIds: ["female", "common"],
    contentTypeIds: [],
    description: "家族の関係や暮らしを描いた作品。",
    sortOrder: 5,
  },
  {
    id: "g-rival",
    name: "ライバル",
    kana: "らいばる",
    aliases: ["競争相手", "宿敵"],
    providerGenreId: "GN-2006",
    providerFloorId: "FL-02",
    floorId: "fl-char",
    audienceIds: ["male", "common"],
    contentTypeIds: [],
    description: "互いに高め合う関係を描いた作品。",
    sortOrder: 6,
  },
  {
    id: "g-workplace",
    name: "仕事仲間",
    kana: "しごとなかま",
    aliases: ["同僚", "チーム"],
    providerGenreId: "GN-2007",
    providerFloorId: "FL-02",
    floorId: "fl-char",
    audienceIds: ["female", "common"],
    contentTypeIds: [],
    description: "仕事を通じて関係が深まる作品。",
    sortOrder: 7,
  },
  {
    id: "g-mentor",
    name: "師弟",
    kana: "してい",
    aliases: ["師匠", "教え"],
    providerGenreId: "GN-2008",
    providerFloorId: "FL-02",
    floorId: "fl-char",
    audienceIds: ["male", "female", "common"],
    contentTypeIds: [],
    description: "教える者と学ぶ者の関係を描いた作品。",
    sortOrder: 8,
  },
  {
    id: "g-quiet",
    name: "静かな物語",
    kana: "しずかなものがたり",
    aliases: ["静謐", "しっとり"],
    providerGenreId: "GN-3001",
    providerFloorId: "FL-03",
    floorId: "fl-mood",
    audienceIds: ["male", "female", "common"],
    contentTypeIds: [],
    description: "大きな起伏よりも空気感を味わう作品。",
    sortOrder: 1,
  },
  {
    id: "g-long",
    name: "長編",
    kana: "ちょうへん",
    aliases: ["じっくり", "大作"],
    providerGenreId: "GN-3002",
    providerFloorId: "FL-03",
    floorId: "fl-mood",
    audienceIds: ["male", "common"],
    contentTypeIds: [],
    description: "時間をかけて読み進めるボリュームのある作品。",
    sortOrder: 2,
  },
  {
    id: "g-short",
    name: "短編",
    kana: "たんぺん",
    aliases: ["読み切り", "ショート"],
    providerGenreId: "GN-3003",
    providerFloorId: "FL-03",
    floorId: "fl-mood",
    audienceIds: ["male", "female", "common"],
    contentTypeIds: [],
    description: "短い時間で読み切れる作品。",
    sortOrder: 3,
  },
  {
    id: "g-anthology",
    name: "短編集",
    kana: "たんぺんしゅう",
    aliases: ["オムニバス", "連作"],
    providerGenreId: "GN-3004",
    providerFloorId: "FL-03",
    floorId: "fl-mood",
    audienceIds: ["male", "female", "common"],
    contentTypeIds: [],
    description: "複数の短編を収録した作品。",
    sortOrder: 4,
  },
  {
    id: "g-adult",
    name: "大人向け",
    kana: "おとなむけ",
    aliases: ["大人", "アダルト"],
    providerGenreId: "GN-3005",
    providerFloorId: "FL-03",
    floorId: "fl-mood",
    audienceIds: ["female", "common"],
    contentTypeIds: [],
    description: "落ち着いた年齢層に向けた作品。",
    sortOrder: 5,
  },
  {
    id: "g-nostalgic",
    name: "ノスタルジー",
    kana: "のすたるじー",
    aliases: ["懐かしい", "郷愁"],
    providerGenreId: "GN-3006",
    providerFloorId: "FL-03",
    floorId: "fl-mood",
    audienceIds: ["male", "female", "common"],
    contentTypeIds: [],
    description: "過去の記憶や風景を感じさせる作品。",
    sortOrder: 6,
  },
  {
    id: "g-night",
    name: "夜",
    kana: "よる",
    aliases: ["深夜", "ナイト"],
    providerGenreId: "GN-3007",
    providerFloorId: "FL-03",
    floorId: "fl-mood",
    audienceIds: ["male", "female", "common"],
    contentTypeIds: [],
    description: "夜の時間帯や夜景を印象的に扱う作品。",
    sortOrder: 7,
  },
  {
    id: "g-summer",
    name: "夏",
    kana: "なつ",
    aliases: ["真夏", "夏休み"],
    providerGenreId: "GN-3008",
    providerFloorId: "FL-03",
    floorId: "fl-mood",
    audienceIds: ["female", "common"],
    contentTypeIds: [],
    description: "夏の季節感をまとった作品。",
    sortOrder: 8,
  },
  {
    id: "g-winter",
    name: "冬",
    kana: "ふゆ",
    aliases: ["冬の日", "ホワイト"],
    providerGenreId: "GN-3009",
    providerFloorId: "FL-03",
    floorId: "fl-mood",
    audienceIds: ["female", "common"],
    contentTypeIds: [],
    description: "冬の季節感をまとった作品。",
    sortOrder: 9,
  },
  {
    id: "g-rain",
    name: "雨",
    kana: "あめ",
    aliases: ["雨上がり", "雨天"],
    providerGenreId: "GN-3010",
    providerFloorId: "FL-03",
    floorId: "fl-mood",
    audienceIds: ["male", "female", "common"],
    contentTypeIds: [],
    description: "雨の描写が印象的な作品。",
    sortOrder: 10,
  },
  {
    id: "g-cafe",
    name: "カフェ",
    kana: "かふぇ",
    aliases: ["喫茶店", "cafe"],
    providerGenreId: "GN-3011",
    providerFloorId: "FL-03",
    floorId: "fl-mood",
    audienceIds: ["female", "common"],
    contentTypeIds: [],
    description: "カフェや喫茶店を舞台にした作品。",
    sortOrder: 11,
  },
  {
    id: "g-library",
    name: "図書館",
    kana: "としょかん",
    aliases: ["本", "書庫"],
    providerGenreId: "GN-3012",
    providerFloorId: "FL-03",
    floorId: "fl-mood",
    audienceIds: ["female", "common"],
    contentTypeIds: [],
    description: "図書館や本を題材にした作品。",
    sortOrder: 12,
  },
  {
    id: "g-travel",
    name: "旅",
    kana: "たび",
    aliases: ["紀行", "トラベル"],
    providerGenreId: "GN-3013",
    providerFloorId: "FL-03",
    floorId: "fl-mood",
    audienceIds: ["male", "female", "common"],
    contentTypeIds: [],
    description: "移動や滞在の時間を描いた作品。",
    sortOrder: 13,
  },
  {
    id: "g-musicdrama",
    name: "ドラマCD",
    kana: "どらましーでぃー",
    aliases: ["CD", "サウンドドラマ"],
    providerGenreId: "GN-4001",
    providerFloorId: "FL-04",
    floorId: "fl-media",
    audienceIds: ["male", "female", "common"],
    contentTypeIds: ["voice"],
    description: "音声ドラマとして収録された作品。",
    sortOrder: 1,
  },
  {
    id: "g-photobook",
    name: "写真集",
    kana: "しゃしんしゅう",
    aliases: ["フォトブック", "ビジュアル"],
    providerGenreId: "GN-4002",
    providerFloorId: "FL-04",
    floorId: "fl-media",
    audienceIds: ["female", "common"],
    contentTypeIds: ["book", "ebook"],
    description: "写真を中心に構成された作品。",
    sortOrder: 2,
  },
  {
    id: "g-essay",
    name: "エッセイ",
    kana: "えっせい",
    aliases: ["随筆", "コラム"],
    providerGenreId: "GN-4003",
    providerFloorId: "FL-04",
    floorId: "fl-media",
    audienceIds: ["female", "common"],
    contentTypeIds: ["book", "ebook"],
    description: "体験や考えを文章で綴った作品。",
    sortOrder: 3,
  },
  {
    id: "g-voice",
    name: "ボイスドラマ",
    kana: "ぼいすどらま",
    aliases: ["音声", "ボイス"],
    providerGenreId: "GN-4004",
    providerFloorId: "FL-04",
    floorId: "fl-media",
    audienceIds: ["male", "female", "common"],
    contentTypeIds: ["voice"],
    description: "音声のみで物語を届ける作品。",
    sortOrder: 4,
  },
  {
    id: "g-anthology-mag",
    name: "アンソロジー",
    kana: "あんそろじー",
    aliases: ["合同誌", "寄稿"],
    providerGenreId: "GN-4005",
    providerFloorId: "FL-04",
    floorId: "fl-media",
    audienceIds: ["male", "female", "common"],
    contentTypeIds: ["doujin", "manga", "ebook"],
    description: "複数の作者による寄稿をまとめた作品。",
    sortOrder: 5,
  },
  {
    id: "g-series",
    name: "シリーズ作品",
    kana: "しりーずさくひん",
    aliases: ["シリーズ", "続編"],
    providerGenreId: "GN-4006",
    providerFloorId: "FL-04",
    floorId: "fl-media",
    audienceIds: ["male", "female", "common"],
    contentTypeIds: [],
    description: "複数の巻や続編を持つ作品。",
    sortOrder: 6,
  },
  {
    id: "g-collection",
    name: "コレクション",
    kana: "これくしょん",
    aliases: ["限定", "グッズ"],
    providerGenreId: "GN-5001",
    providerFloorId: "FL-05",
    floorId: "fl-collect",
    audienceIds: ["common"],
    contentTypeIds: ["goods"],
    description: "飾る・集めることを目的とした商品。",
    sortOrder: 1,
  },
  {
    id: "g-figure",
    name: "フィギュア",
    kana: "ふぃぎゅあ",
    aliases: ["立体物", "モデル"],
    providerGenreId: "GN-5002",
    providerFloorId: "FL-05",
    floorId: "fl-collect",
    audienceIds: ["common"],
    contentTypeIds: ["goods"],
    description: "立体造形の商品。",
    sortOrder: 2,
  },
  {
    id: "g-apparel",
    name: "アパレル",
    kana: "あぱれる",
    aliases: ["衣類", "Tシャツ"],
    providerGenreId: "GN-5003",
    providerFloorId: "FL-05",
    floorId: "fl-collect",
    audienceIds: ["common"],
    contentTypeIds: ["goods"],
    description: "服飾系の商品。",
    sortOrder: 3,
  },
  {
    id: "g-stationery",
    name: "文具",
    kana: "ぶんぐ",
    aliases: ["雑貨", "ステーショナリー"],
    providerGenreId: "GN-5004",
    providerFloorId: "FL-05",
    floorId: "fl-collect",
    audienceIds: ["common"],
    contentTypeIds: ["goods"],
    description: "文房具や小物の商品。",
    sortOrder: 4,
  },
];

export const attributes: AttributeDefinition[] = [
  { id: "at-volume", name: "収録時間", unit: "分", valueType: "number", contentTypeIds: ["video"] },
  { id: "at-pages", name: "ページ数", unit: "ページ", valueType: "number", contentTypeIds: ["manga", "ebook", "book", "doujin"] },
  { id: "at-format", name: "ファイル形式", unit: null, valueType: "text", contentTypeIds: [] },
  { id: "at-platform", name: "対応環境", unit: null, valueType: "text", contentTypeIds: ["game", "cg"] },
  { id: "at-size", name: "サイズ", unit: "mm", valueType: "size", contentTypeIds: ["goods"] },
  { id: "at-tracks", name: "収録数", unit: "トラック", valueType: "number", contentTypeIds: ["voice"] },
];

export const contentTypes: ContentTypeDefinition[] = [
  { id: "video", name: "動画", shortName: "動画", icon: "ri-film-line", description: "配信・パッケージの映像作品。" },
  { id: "manga", name: "漫画", shortName: "漫画", icon: "ri-book-2-line", description: "連載・読み切りを含む漫画作品。" },
  { id: "ebook", name: "電子書籍", shortName: "電子書籍", icon: "ri-tablet-line", description: "小説・短編集などの電子書籍。" },
  { id: "book", name: "書籍", shortName: "書籍", icon: "ri-book-open-line", description: "紙の書籍・写真集・エッセイ。" },
  { id: "doujin", name: "同人", shortName: "同人", icon: "ri-quill-pen-line", description: "同人誌・自主制作の作品。" },
  { id: "voice", name: "音声", shortName: "音声", icon: "ri-mic-line", description: "ドラマCD・ボイスドラマ。" },
  { id: "cg", name: "CG", shortName: "CG", icon: "ri-image-2-line", description: "CG集・イラスト集。" },
  { id: "game", name: "ゲーム", shortName: "ゲーム", icon: "ri-gamepad-line", description: "PC・家庭用のゲーム作品。" },
  { id: "goods", name: "グッズ", shortName: "グッズ", icon: "ri-shopping-bag-3-line", description: "フィギュア・アパレルなどの商品。" },
  { id: "other", name: "その他", shortName: "その他", icon: "ri-apps-2-line", description: "上記に当てはまらない作品。" },
];

export const audiences: AudienceDefinition[] = [
  { id: "male", name: "男性向け", shortName: "男性向け", description: "男性向けとして登録された作品・ジャンル。" },
  { id: "female", name: "女性向け", shortName: "女性向け", description: "女性向けとして登録された作品・ジャンル。" },
  { id: "common", name: "共通", shortName: "共通", description: "どちらのモードでも表示される作品・ジャンル。" },
  { id: "unclassified", name: "未分類", shortName: "未分類", description: "対象区分が未設定の取り込みデータ。" },
];